import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {provideRouter, withInMemoryScrolling} from '@angular/router';
import {appRoutes} from './app.routes';
import {providePrimeNG} from 'primeng/config';
import {provideStore} from '@ngrx/store';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations';
import {includeBearerTokenInterceptor} from 'keycloak-angular';
import {provideKeycloakAngular} from './keycloak.config';
import {APP_PROD_MODE, errorInterceptor, unauthorizedInterceptor} from '@frontend/shared';
import {provideTranslocoConfig} from '@frontend/transloco';
import {NgxEchartsModule} from 'ngx-echarts';

import {DIRECTUS_URL, DirectusLoader} from '@frontend/directus';
import {BASE_PATH} from '@frontend/shared-api';
import {AtlantisPreset} from './atlantis.config';
import {ToastModule} from 'primeng/toast';
import {ConfirmationService, MessageService} from 'primeng/api';
import {environment} from '../environments/environment';
import {TranslocoService} from '@jsverse/transloco';
import {lastValueFrom} from 'rxjs';
import {StagingAuthService} from './shared/services/staging-auth.service';
import {ProjectStepStateService} from './pages/customer/project-sequence/project-step-state.service';
import {DialogService} from 'primeng/dynamicdialog';
import {providerTenantInterceptor} from './shared/interceptors/provider-tenant.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: DIRECTUS_URL,
      useValue: environment.directusUrl,
    },
    {
      provide: APP_PROD_MODE,
      useValue: environment.production,
    },
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(appRoutes, withInMemoryScrolling({scrollPositionRestoration: 'enabled'})),
    provideAnimations(),
    provideKeycloakAngular(),
    providePrimeNG({
      theme: {
        preset: AtlantisPreset,
        options: {darkModeSelector: '.app-dark'},
      },
    }),
    MessageService,
    ConfirmationService,
    importProvidersFrom(ToastModule),
    provideStore(),
    provideHttpClient(
      withInterceptors([
        includeBearerTokenInterceptor,
        providerTenantInterceptor,
        errorInterceptor,
        unauthorizedInterceptor,
      ]),
    ),
    importProvidersFrom(
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts'),
      }),
    ),
    provideTranslocoConfig(),
    provideAppInitializer(() => initializeLanguages()),
    {provide: BASE_PATH, useValue: '/api'},
    StagingAuthService,
    ProjectStepStateService,
    DialogService,
  ],
};

function initializeLanguages() {
  const directusLoader = inject(DirectusLoader);
  const translocoService = inject(TranslocoService);

  return directusLoader.loadLanguages().then(() => {
    const langs = directusLoader.availableLanguages.map((lang) => lang.code);
    translocoService.setAvailableLangs(langs);

    const activeLang = getDefaultLanguage(langs);
    translocoService.setActiveLang(activeLang);
    return lastValueFrom(translocoService.load(activeLang));
  });
}

function getDefaultLanguage(langs: string[]): string {
  const savedLang = localStorage.getItem('transloco-lang');
  if (savedLang && langs.includes(savedLang)) {
    return savedLang;
  }
  const browserLang = navigator.language.split('-')[0];
  return langs.includes(browserLang) ? browserLang : langs[0];
}
